import React from "react";
import { cn } from "@/Components/ui/utils";

const DEFAULT_STEPS = [
  { label: "날짜", value: 0 },
  { label: "조회", value: 25 },
  { label: "상세", value: 50 },
  { label: "옵션", value: 75 },
  { label: "결제", value: 100 },
];

/**
 * 예약/결제 플로우 공용 단계 진행바.
 * - 닷(진행 헤드)·채움·라벨이 동일한 단계 좌표(0·1·2·3 균등 분포)를 공유해 세로로 정확히 정렬됨
 * - 도달 구간(주황)에만 sheen 흐름 애니메이션을 둬 살아있게 표현하고, 미도달 구간(회색)은 정적으로 유지
 */
export default function StepProgressBar({ progressPercent = 0, steps = DEFAULT_STEPS, className }) {
  const lastIndex = Math.max(steps.length - 1, 1);
  // 각 단계의 트랙상 위치(%) — 균등 분포. 닷·라벨이 같은 값을 써서 세로 정렬을 보장한다.
  const stepPositions = steps.map((_, i) => (i / lastIndex) * 100);
  // 현재 도달한 마지막 단계 인덱스 (닷은 이 단계 라인 위에 스냅된다)
  const reachedIndex = steps.reduce(
    (acc, step, i) => (progressPercent >= step.value ? i : acc),
    0
  );
  const headPosition = stepPositions[reachedIndex];

  return (
    <div className={cn("flex flex-col", className)}>
      {/* 좌우 여백: 양 끝 라벨의 중심이 트랙의 0%/100%와 정확히 정렬되도록 안전 영역 확보 */}
      <div className="relative px-3">
        <div className="relative h-1.5 w-full">
          {/* 미도달 구간(회색 트랙) — 애니메이션 없음 */}
          <div className="absolute inset-0 overflow-hidden rounded-full bg-gray-200">
            {/* 도달 구간(주황 채움) — sheen 흐름 애니메이션 유지 */}
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-[linear-gradient(90deg,#f4c8cf_0%,#f7b08a_42%,#e16036_100%)] transition-[width] duration-700 ease-out"
              style={{ width: `${headPosition}%` }}
            >
              {headPosition > 0 && (
                <span className="progress-sheen pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.45),transparent)]" />
              )}
            </div>
          </div>
          {/* 진행 헤드(닷) — 현재 단계 라인 위에 중심 정렬, 정적 글로우 */}
          <span
            className="progress-head pointer-events-none absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white transition-[left] duration-700 ease-out"
            style={{ left: `${headPosition}%` }}
          />
        </div>
        {/* 단계 라벨 — 각 단계 위치에 중심 정렬 */}
        <div className="relative mt-2 h-4">
          {steps.map((step, i) => {
            const reached = progressPercent >= step.value;
            const isCurrent = i === reachedIndex;
            return (
              <span
                key={step.label}
                className={cn(
                  "absolute top-0 -translate-x-1/2 whitespace-nowrap text-[11px] font-[Gmarket-M] transition-[color,transform] duration-300 ease-out",
                  isCurrent
                    ? "-translate-y-0.5 font-bold text-[#c2410c]"
                    : reached
                      ? "font-semibold text-[#dd8a63]"
                      : "text-gray-400"
                )}
                style={{ left: `${stepPositions[i]}%` }}
              >
                {step.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
